import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        role: null,
    });
    const [loading, setLoading] = useState(true);
    // Memoize the function with useCallback to avoid infinite calls
    const refreshAuthStatus = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_hostURL}/auth/status`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });

            if (response.status === 401 || response.status === 204) {
                setAuthState({ user: null, isAuthenticated: false, isAdmin: false, role: '' });
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setAuthState({
                    user: data.user,
                    isAuthenticated: data.isAuthenticated,
                    isAdmin: data.user.isAdmin,
                    role: data.user.role,
                });
            } else {
                setAuthState({ user: null, isAuthenticated: false, isAdmin: false });
            }
        } catch (error) {
            console.log('Failed to fetch auth status:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Use useEffect with the memoized function
    useEffect(() => {
        refreshAuthStatus();
    }, [refreshAuthStatus]);

    // Without this, `{...authState, loading, refreshAuthStatus}` is a new
    // object on every render, which re-renders every consumer (Header,
    // Login, Signup, ...) even when nothing actually changed.
    const value = useMemo(
        () => ({ ...authState, loading, refreshAuthStatus }),
        [authState, loading, refreshAuthStatus]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
